import { useCallback, useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n/LangContext";

/**
 * Drag the vertical handle (or click anywhere on the image) to reveal
 * the alternative glass state. Used for the PrivacyGlass Premium showcase.
 *
 * The TOP image (frosted/opaque) is clipped horizontally from the left.
 * At position=0% you see the underlying clear image; at 100% the frosted
 * fully covers it.
 */
export default function GlassSlider({
  clearSrc,
  opaqueSrc,
  initial = 55,
  reverse = false,
  testId = "glass-slider",
}) {
  const { t } = useLang();
  const containerRef = useRef(null);
  const [position, setPosition] = useState(initial); // 0 - 100
  const draggingRef = useRef(false);

  // When `reverse` is true, the
  // CLEAR image is the top layer and the OPAQUE image is the base. The
  // left side of the divider therefore shows the clear state and the
  // right side shows the activated frosted state.
  const baseSrc = reverse ? opaqueSrc : clearSrc;
  const topSrc = reverse ? clearSrc : opaqueSrc;
  const baseAlt = reverse
    ? "Frosted smart glass state"
    : "Clear smart glass state";
  const topAlt = reverse
    ? "Clear smart glass state"
    : "Frosted smart glass state";
  const leftPillLabel = reverse ? t("hero.status_off") : t("hero.status_on");
  const rightPillLabel = reverse ? t("hero.status_on") : t("hero.status_off");
  const leftPillDot = reverse ? "bg-[#34C759]" : "bg-[#86868B]";
  const rightPillDot = reverse ? "bg-[#86868B]" : "bg-[#34C759]";

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, pct)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [setFromClientX]);

  // Trigger the "drag me" sway the first time the slider enters the viewport
  // (intentionally disabled — no hint label / sway).

  const onDown = (e) => {
    draggingRef.current = true;
    document.body.style.userSelect = "none";
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(x);
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") {
      setPosition((p) => Math.max(0, p - 4));
    }
    if (e.key === "ArrowRight") {
      setPosition((p) => Math.min(100, p + 4));
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className="relative w-full h-[420px] md:h-[560px] rounded-3xl overflow-hidden bg-[#F5F5F7] select-none cursor-ew-resize"
      onMouseDown={onDown}
      onTouchStart={onDown}
    >
      {/* Base layer */}
      <img
        src={baseSrc}
        alt={baseAlt}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable="false"
      />
      {/* Top layer, clipped from the LEFT up to position% */}
      <img
        src={topSrc}
        alt={topAlt}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable="false"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      />

      {/* State pills */}
      <div className="absolute top-5 left-5 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-white/70 shadow-sm">
        <span className={`h-1.5 w-1.5 rounded-full ${leftPillDot}`} />
        <span className="text-[10px] font-body font-semibold tracking-[0.22em] uppercase text-[#1D1D1F]/80">
          {leftPillLabel}
        </span>
      </div>
      <div className="absolute top-5 right-5 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-white/70 shadow-sm">
        <span className={`h-1.5 w-1.5 rounded-full ${rightPillDot}`} />
        <span className="text-[10px] font-body font-semibold tracking-[0.22em] uppercase text-[#1D1D1F]/80">
          {rightPillLabel}
        </span>
      </div>

      {/* Divider line */}
      <div
        aria-hidden
        className="absolute top-0 bottom-0 w-px bg-white/90 shadow-[0_0_18px_rgba(0,0,0,0.18)] pointer-events-none"
        style={{ left: `${position}%` }}
      />

      {/* Handle */}
      <button
        type="button"
        data-testid={`${testId}-handle`}
        aria-label="Smart glass comparison slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        role="slider"
        onKeyDown={onKey}
        onMouseDown={(e) => {
          e.stopPropagation();
          onDown(e);
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          onDown(e);
        }}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white border border-black/10 shadow-[0_8px_28px_rgba(0,0,0,0.18)] flex items-center justify-center gap-0.5 cursor-ew-resize hover:scale-[1.06] active:scale-[0.98] transition-transform"
        style={{ left: `${position}%` }}
      >
        <svg width="10" height="14" viewBox="0 0 10 14" fill="none" stroke="#1D1D1F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="7,2 2,7 7,12" />
        </svg>
        <svg width="10" height="14" viewBox="0 0 10 14" fill="none" stroke="#1D1D1F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="3,2 8,7 3,12" />
        </svg>
      </button>
    </div>
  );
}
